import { z } from 'zod';
import { ChannelId } from './common.js';

/**
 * A trade moves ITEMS. It has no currency field, at all.
 *
 * That is barrier four of currency isolation: cross-channel value transfer is
 * not forbidden by a guard that could be forgotten, it is unrepresentable. If
 * the product ever wants "cash plus card for card", that is two operations —
 * a channel-scoped purchase and a trade, each atomic, each with its own
 * receipt — and merging them should be pushed back on hard.
 */
export const TradeStatus = z.enum(['draft', 'pending_settle', 'settled', 'cancelled']);

/**
 * Every offer names its RECIPIENT.
 *
 * This one choice makes every shape work with no special cases and no
 * "whose side is this" matching:
 *
 *   2-party swap   A→B, B→A
 *   3-way ring     A→B, B→C, C→A
 *   pot split      A→C, A→D, B→C
 *
 * Settlement is then a pure function over the graph.
 */
export const TradeOffer = z.object({
  instance_id: z.string(),
  to_uid: z.string(),
});

export const TradeSide = z.object({
  uid: z.string(),
  offers: z.array(TradeOffer).max(50),
  /** Null until they confirm. Cleared for EVERYONE whenever any offer changes. */
  confirmed_at: z.string().datetime().nullable(),
});

export const Trade = z.object({
  id: z.string(),
  status: TradeStatus,
  participants: z.array(z.string()).min(2).max(6),
  /**
   * The only collection without a single channel_id. It carries the list
   * because it is the cross-tenant object by definition — documenting the
   * exception beats fudging it.
   */
  channel_ids: z.array(ChannelId),
  sides: z.array(TradeSide),
  /**
   * sha256 over every side's offers, canonically ordered.
   *
   * A participant confirms a HASH, not a moment. Any edit to any side changes
   * it and invalidates every confirmation — which is both the correct escrow
   * semantic and the fix for a last-second swap. It is the shape of the data
   * rather than a check somebody has to remember.
   */
  offer_hash: z.string(),
  version: z.number().int().min(0),
  created_at: z.string().datetime(),
  /** Drafts expire; a confirmed trade waits for settle or an explicit cancel. */
  expires_at: z.string().datetime().nullable(),
  settled_at: z.string().datetime().nullable(),
});

/** Opening a negotiation. The offers come afterwards, one side at a time. */
export const TradeCreate = z.object({
  /** Everyone involved, including you. */
  participants: z.array(z.string().min(1)).min(2).max(6),
});

/**
 * Replacing your whole side.
 *
 * Wholesale, never a partial merge: there is no way to say "remove offer 2"
 * in a merge, and a participant who believes they withdrew an item and did
 * not is the worst possible failure on a screen about trust.
 */
export const TradeOfferInput = z.object({
  offers: z.array(TradeOffer).max(50),
});

export const TradeConfirm = z.object({
  /** The hash you are agreeing to. Refused if the trade has moved on. */
  offer_hash: z.string().min(1),
});

export type Trade = z.infer<typeof Trade>;
export type TradeOffer = z.infer<typeof TradeOffer>;
export type TradeSide = z.infer<typeof TradeSide>;
export type TradeCreate = z.infer<typeof TradeCreate>;
export type TradeOfferInput = z.infer<typeof TradeOfferInput>;
